import { Eye, Users, Heart, TrendingUp, UserPlus, Timer, Clock, Pencil } from 'lucide-react';
import {
  METRIC_FIELDS,
  normalizeMetrics,
  getEngagementRate,
  summarizeMetrics,
  formatCompactNumber,
  formatDuration,
  formatMetricValue,
  formatPercent,
  type ChPostMetrics,
  type MetricsItem,
  type MetricsSummary,
  type BestTime,
} from '../lib/metrics';
import { PLATFORM_LABELS, type ChFollowers, type ChPlatformMetrics, type ChPostPlatform } from '../types';
import { getTotalFollowers } from '../lib/posts';

interface Palette {
  text: string;
  muted: string;
  surface: string;
}

const HUB_PALETTE: Palette = { text: 'var(--ch-ink)', muted: '#6b7280', surface: '#ffffff' };

const PLATFORM_SHORT: Record<ChPostPlatform, string> = { instagram: 'IG', tiktok: 'TT', youtube: 'YT', podcast: 'Pod' };

function followersBreakdown(followers: ChFollowers | null | undefined): string | null {
  const entries = Object.entries(followers || {}).filter(([, n]) => typeof n === 'number') as [ChPostPlatform, number][];
  if (entries.length < 2) return null;
  return entries.map(([p, n]) => `${PLATFORM_SHORT[p] || p} ${formatCompactNumber(n)}`).join(' · ');
}

/**
 * Totals for a project: published items, followers, views, reach,
 * interactions, rate, followers gained, watch time and the best time to post.
 */
export function MetricsSummaryStrip({
  summary,
  publishedCount,
  followers,
  bestTime,
  onEditFollowers,
  palette = HUB_PALETTE,
}: {
  summary: MetricsSummary;
  publishedCount: number;
  followers?: ChFollowers | null;
  bestTime?: BestTime | null;
  /** Staff: the followers tile becomes editable. */
  onEditFollowers?: () => void;
  palette?: Palette;
}) {
  const { totals } = summary;
  const followerTotal = getTotalFollowers(followers);
  // hint: a line under the value; tooltip: on hover only
  const tiles: { label: string; value: string; icon: typeof Eye; hint?: string | null; tooltip?: string; onEdit?: () => void }[] = [
    { label: 'Publicados', value: String(publishedCount), icon: TrendingUp },
  ];
  if (followerTotal !== null || onEditFollowers) {
    tiles.push({
      label: 'Seguidores',
      value: followerTotal !== null ? formatCompactNumber(followerTotal) : '—',
      icon: Users,
      hint: followersBreakdown(followers),
      onEdit: onEditFollowers,
    });
  }
  if (totals.views !== undefined) tiles.push({ label: 'Visualizações', value: formatCompactNumber(totals.views), icon: Eye });
  if (totals.reach !== undefined) tiles.push({ label: 'Alcance', value: formatCompactNumber(totals.reach), icon: Users });
  if (summary.engagement > 0) tiles.push({ label: 'Interações', value: formatCompactNumber(summary.engagement), icon: Heart, tooltip: 'Gostos + comentários + partilhas + guardados' });
  if (summary.engagementRate !== null) tiles.push({ label: 'Taxa de envolvimento', value: formatPercent(summary.engagementRate), icon: TrendingUp });
  if (totals.followers_gained !== undefined) tiles.push({ label: 'Seguidores ganhos', value: `+${formatCompactNumber(totals.followers_gained)}`, icon: UserPlus });
  if (totals.avg_watch_time !== undefined) tiles.push({ label: 'Tempo médio de visualização', value: formatDuration(totals.avg_watch_time), icon: Timer });
  if (bestTime) tiles.push({ label: 'Melhor horário', value: bestTime.label, icon: Clock, hint: `${formatPercent(bestTime.rate)} de envolvimento em ${bestTime.items} publicações` });

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {tiles.map(tile => (
        <div key={tile.label} className="relative rounded-2xl px-4 py-3" style={{ backgroundColor: palette.surface }} title={tile.tooltip || tile.hint || undefined}>
          <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide" style={{ color: palette.muted }}>
            <tile.icon className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{tile.label}</span>
          </div>
          <p className="text-2xl font-bold mt-1 tabular-nums" style={{ color: palette.text }}>{tile.value}</p>
          {tile.hint && (
            <p className="text-[10px] mt-0.5 truncate" style={{ color: palette.muted }}>{tile.hint}</p>
          )}
          {tile.onEdit && (
            <button
              type="button"
              onClick={tile.onEdit}
              aria-label="Editar seguidores"
              className="absolute top-2 right-2 p-1 rounded-lg text-gray-400 hover:text-[var(--ch-ink)] hover:bg-gray-100"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/** Every filled stat of one item, plus interactions and engagement rate. */
export function PostMetricsGrid({
  metrics,
  byPlatform,
  palette = HUB_PALETTE,
}: {
  metrics: MetricsItem;
  /** Shows one line per platform under the totals. */
  byPlatform?: ChPlatformMetrics | null;
  palette?: Palette;
}) {
  const summary = summarizeMetrics([metrics]);
  const m: ChPostMetrics = summary.totals;
  const filled = METRIC_FIELDS.filter(f => m[f.key] !== undefined);
  if (filled.length === 0) return null;
  const platforms = Object.entries(byPlatform || {}).filter(([, v]) => v && Object.keys(normalizeMetrics(v)).length > 0) as [ChPostPlatform, ChPostMetrics][];

  const tiles = filled.map(f => ({ key: f.key, label: f.label, value: formatMetricValue(f.key, m[f.key] as number) }));
  if (summary.engagement > 0) tiles.splice(Math.min(tiles.length, 3), 0, { key: 'likes', label: 'Interações', value: formatCompactNumber(summary.engagement) });
  if (summary.engagementRate !== null) tiles.push({ key: 'likes', label: 'Envolvimento', value: formatPercent(summary.engagementRate) });

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        {tiles.map(t => (
          <div key={t.label} className="rounded-xl px-2.5 py-2" style={{ backgroundColor: palette.surface }}>
            <p className="text-[10px] font-medium uppercase tracking-wide truncate" style={{ color: palette.muted }} title={t.label}>{t.label}</p>
            <p className="text-sm font-bold tabular-nums" style={{ color: palette.text }}>{t.value}</p>
          </div>
        ))}
      </div>
      {platforms.length > 1 && (
        <ul className="text-[11px] space-y-0.5" style={{ color: palette.muted }}>
          {platforms.map(([p, pm]) => {
            const n = normalizeMetrics(pm);
            const headline = n.views ?? n.reach;
            const rate = getEngagementRate(n);
            return (
              <li key={p}>
                <strong style={{ color: palette.text }}>{PLATFORM_LABELS[p] || p}</strong>
                {headline !== undefined && <> · {formatCompactNumber(headline)} {n.views !== undefined ? 'visualizações' : 'alcance'}</>}
                {rate !== null && <> · {formatPercent(rate)}</>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** One-line teaser for cards: views (or reach) and engagement rate. */
export function PostMetricsBadge({ metrics }: { metrics: ChPostMetrics | null | undefined }) {
  const m = normalizeMetrics(metrics);
  const headline = m.views ?? m.reach;
  if (headline === undefined) return null;
  const rate = getEngagementRate(m);
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--ch-ink)]">
      <Eye className="w-3 h-3" />
      {formatCompactNumber(headline)}
      {rate !== null && <span className="text-gray-400 font-medium">· {formatPercent(rate)}</span>}
    </span>
  );
}
